import type {
    ArrayContentDescription,
    ArrayScreenFormat,
    ComponentDescription,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    TableGlideType,
} from "../common/description";
import type { Doc } from "../common/docUrl";
import type { FeatureSettingName } from "../common/feature-settings";
import type {
    AppDescriptionContext,
    ComponentSpecialCaseDescriptor,
    InteractiveComponentConfiguratorContext,
} from "../component-utils";
import type { InlineListComponentDescription } from "../component-utils/inline-list";
import type { WireComponentKind } from "../wire/component-kind";
import type { WireAction, WireComponent, WireEditableValue, WirePaging } from "../wire/types";
import type { ActionSpec, FluentPropertiesSpec } from "./fluent-properties-spec";
import { FluentProperties } from "./fluent-properties-spec";

export type PropertiesOfComponent<T> = T extends FluentComponent<infer U, infer V, unknown> ? U & V : never;
type AsFormProperties<T> = { readonly [K in keyof T]?: WireEditableValue<NonNullable<T[K]>> };
export type WirePropertiesOfComponent<T> = T extends FluentComponent<infer U, infer V, infer W>
    ? WireComponent & W & U & AsFormProperties<V>
    : never;

type PropertiesOfArrayContent<T> = T extends FluentArrayContent<infer U, infer V, unknown> ? U & V : never;
export type WirePropertiesOfArrayContent<T> = T extends FluentArrayContent<infer U, infer V, infer W>
    ? W & V & WireComponent & { readonly items: readonly U[] }
    : never;

// TODO: We're not forcing the correct `kind` here.  We'd have to add another
// generic argument to `FluentComponent` to be the component kind, then we
// could use that here as the `kind` type.
export type DescriptionOfComponent<T> = ComponentDescription &
    {
        readonly [K in keyof PropertiesOfComponent<T>]?: PropertyDescription;
    };

export type ArrayContentDescriptionForProperties<T> = ArrayContentDescription &
    { [K in keyof T]: PropertyDescription | undefined };
//  type DescriptionOfArrayContent<T> = T extends FluentArrayContent<infer U>
//     ? ArrayContentDescriptionForProperties<U>
//     : never;

export type DescriptionOfArrayContentInlineList<T> = InlineListComponentDescription &
    {
        readonly [K in keyof PropertiesOfArrayContent<T>]?: PropertyDescription;
    };

export enum ContainerKind {
    // The subcomponents are all part of the same context as the containing
    // screen.
    Regular = "regular",
    // The container has an output row from a potentially different table.
    Form = "form",
}

export type FluentComponentSpecializer = (
    desc: ComponentDescription,
    tables: InputOutputTables,
    iccc: InteractiveComponentConfiguratorContext,
    mutatingScreenKind: MutatingScreenKind | undefined,
    insideContainer: boolean
) => ComponentDescription;
type FluentComponentSpecialCases = readonly [ComponentSpecialCaseDescriptor, FluentComponentSpecializer][];

export interface FluentComponentSpec {
    readonly kind: WireComponentKind;

    readonly displayName?: string;
    readonly description?: string;
    readonly group?: string;
    readonly icon?: string;
    readonly docURL?: Doc;
    readonly containerKind?: ContainerKind;
    readonly properties: FluentPropertiesSpec;
    readonly formProperties: FluentPropertiesSpec;
    readonly actions?: readonly ActionSpec[];
    readonly configuresScreenTitle?: boolean;
    readonly featureSetting?: FeatureSettingName;
    readonly specialCases?: FluentComponentSpecialCases;
    readonly includeRegularComponent: boolean;
}

export class FluentComponent<TProps, TForm, TWire> {
    constructor(public readonly spec: FluentComponentSpec) {}

    public withProperties<T>(props: FluentProperties<T, unknown>): FluentComponent<TProps & T, TForm, TWire> {
        return new FluentComponent({ ...this.spec, properties: props.spec });
    }

    public withFormProperties<T>(props: FluentProperties<T, unknown>): FluentComponent<TProps, TForm & T, TWire> {
        return new FluentComponent({ ...this.spec, formProperties: props.spec });
    }

    public withDisplayName(displayName: string): FluentComponent<TProps, TForm, TWire> {
        return new FluentComponent({ ...this.spec, displayName });
    }

    public withIcon(icon: string): FluentComponent<TProps, TForm, TWire> {
        return new FluentComponent({ ...this.spec, icon });
    }

    public withDocURL(docURL: Doc): FluentComponent<TProps, TForm, TWire> {
        return new FluentComponent({ ...this.spec, docURL });
    }

    public withinGroup(group: string): FluentComponent<TProps, TForm, TWire> {
        return new FluentComponent({ ...this.spec, group });
    }

    public withDescription(description: string): FluentComponent<TProps, TForm, TWire> {
        return new FluentComponent({ ...this.spec, description });
    }

    public withFeatureSetting(featureSetting: FeatureSettingName): FluentComponent<TProps, TForm, TWire> {
        return new FluentComponent({ ...this.spec, featureSetting });
    }

    public withScreenTitle(): FluentComponent<TProps, TForm, TWire> {
        return new FluentComponent({ ...this.spec, configuresScreenTitle: true });
    }

    public withLegacy(): FluentComponent<TProps, TForm, TWire> {
        return new FluentComponent({ ...this.spec, includeRegularComponent: false });
    }

    public withSpecialCases(
        includeRegularComponent: boolean,
        specialCases: FluentComponentSpecialCases
    ): FluentComponent<TProps, TForm, TWire> {
        return new FluentComponent({ ...this.spec, specialCases, includeRegularComponent });
    }
}

export type FluentArrayContentSpecializer = (
    desc: ComponentDescription,
    tables: InputOutputTables,
    itemTable: TableGlideType | undefined,
    iccc: AppDescriptionContext,
    mutatingScreenKind: MutatingScreenKind | undefined,
    insideContainer: boolean
) => ComponentDescription;
type FluentArrayContentSpecialCases = readonly [ComponentSpecialCaseDescriptor, FluentArrayContentSpecializer][];

interface FluentArrayContentSpec {
    readonly displayName?: string;
    readonly format?: ArrayScreenFormat;
    readonly specialCase?: ComponentSpecialCaseDescriptor;
    readonly itemProperties: FluentPropertiesSpec;
    readonly componentProperties: FluentPropertiesSpec;
    readonly docURL?: Doc;
    readonly specialCases: FluentArrayContentSpecialCases;
}

export class FluentArrayContent<TItemProps, TComponentProps, TWire> {
    constructor(public readonly spec: FluentArrayContentSpec) {}

    public withDisplayName(displayName: string): FluentArrayContent<TItemProps, TComponentProps, TWire> {
        return new FluentArrayContent({ ...this.spec, displayName });
    }

    public withItemProperties<T>(
        makeProps: (p: FluentProperties<unknown, TComponentProps>) => FluentProperties<T, TComponentProps>
    ): FluentArrayContent<T, TComponentProps, TWire> {
        const props = makeProps(new FluentProperties(emptyProperties));
        return new FluentArrayContent({ ...this.spec, itemProperties: props.spec });
    }

    public withComponentProperties<T>(props: FluentProperties<T, unknown>): FluentArrayContent<TItemProps, T, TWire> {
        return new FluentArrayContent({ ...this.spec, componentProperties: props.spec });
    }

    public withSpecialCase(
        specialCase: ComponentSpecialCaseDescriptor
    ): FluentArrayContent<TItemProps, TComponentProps, TWire> {
        return new FluentArrayContent({ ...this.spec, specialCase });
    }

    public withSpecialCases(
        specialCases: FluentArrayContentSpecialCases
    ): FluentArrayContent<TItemProps, TComponentProps, TWire> {
        return new FluentArrayContent({ ...this.spec, specialCases });
    }

    public withDocURL(docURL: Doc): FluentArrayContent<TItemProps, TComponentProps, TWire> {
        return new FluentArrayContent({ ...this.spec, docURL });
    }
}

export const emptyProperties: FluentPropertiesSpec = {
    propertySpecs: [],
    arraySpecs: [],
    actionSpecs: [],
    hasDisplayedProperties: false,
};
export const properties = new FluentProperties(emptyProperties);

export function component<TKind extends WireComponentKind>(kind: TKind) {
    return new FluentComponent<unknown, unknown, { readonly kind: TKind }>({
        kind,
        properties: emptyProperties,
        formProperties: emptyProperties,
        includeRegularComponent: true,
    });
}

export function form<TKind extends WireComponentKind>(kind: TKind) {
    return new FluentComponent<unknown, unknown, { readonly kind: TKind; readonly submitAction?: WireAction }>({
        kind,
        properties: emptyProperties,
        formProperties: emptyProperties,
        includeRegularComponent: true,
    });
}

export interface WireListComponentGeneric<TFormat extends ArrayScreenFormat> extends WireComponent {
    readonly kind: WireComponentKind.List;
    readonly format: TFormat;

    readonly paging?: WirePaging;

    readonly searchBar?: WireEditableValue<string>;

    // [name, count], where `null` means "All"
    readonly dynamicFilterNames?: readonly [string | null, number][];
    readonly dynamicFilterCurrentIndex?: WireEditableValue<number>;
}

export function arrayContent<TFormat extends ArrayScreenFormat>(format: TFormat) {
    return new FluentArrayContent<unknown, unknown, WireListComponentGeneric<TFormat>>({
        format,
        itemProperties: emptyProperties,
        componentProperties: emptyProperties,
        specialCases: [],
    });
}
